import { useState, useEffect } from 'react';
import { sendSMS, sendEmail } from '../../../lib/messaging';

const STORAGE_KEY = 'wm-messaging-settings-v1';

function loadSettings() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function Badge({ configured }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[var(--wm-text-caption)] font-semibold uppercase tracking-wide ${
        configured
          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-gray-100 text-gray-500 dark:bg-[#243348] dark:text-[#7D93AE]'
      }`}
    >
      {configured ? 'Configured' : 'Not configured'}
    </span>
  );
}

function Field({ label, type = 'text', value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-[var(--wm-text-body)] border border-gray-200 dark:border-[#3D5068] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E8BF0] bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[#94A3B8]"
      />
    </div>
  );
}

export default function MessagingTab() {
  const [sms, setSms] = useState({ accountSid: '', authToken: '', fromNumber: '' });
  const [email, setEmail] = useState({ apiKey: '', fromEmail: '' });
  const [smsSaved, setSmsSaved] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [testSmsStatus, setTestSmsStatus] = useState(null);
  const [testEmailStatus, setTestEmailStatus] = useState(null);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const s = loadSettings();
    if (s.sms) setSms(s.sms);
    if (s.email) setEmail(s.email);
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  const smsConfigured = !!(sms.accountSid && sms.authToken && sms.fromNumber);
  const emailConfigured = !!(email.apiKey && email.fromEmail);

  function saveSms() {
    const s = loadSettings();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...s, sms }));
    setSmsSaved(true);
    setTimeout(() => setSmsSaved(false), 2000);
  }

  function saveEmail() {
    const s = loadSettings();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...s, email }));
    setEmailSaved(true);
    setTimeout(() => setEmailSaved(false), 2000);
  }

  async function handleTestSms() {
    if (!sms.fromNumber) return;
    setTestSmsStatus('sending');
    const result = await sendSMS({ to: sms.fromNumber, body: 'WrapMind test SMS — SMS delivery is working!' });
    setTestSmsStatus(result ? 'ok' : 'error');
    setTimeout(() => setTestSmsStatus(null), 3000);
  }

  async function handleTestEmail() {
    if (!email.fromEmail) return;
    setTestEmailStatus('sending');
    const result = await sendEmail({
      to: email.fromEmail,
      subject: 'WrapMind Test Email',
      html: '<p>WrapMind test email — email delivery is working!</p>',
    });
    setTestEmailStatus(result ? 'ok' : 'error');
    setTimeout(() => setTestEmailStatus(null), 3000);
  }

  function testLabel(status, defaultLabel) {
    if (status === 'sending') return 'Sending…';
    if (status === 'ok') return 'Sent!';
    if (status === 'error') return 'Failed';
    return defaultLabel;
  }

  return (
    <div className="space-y-8 max-w-xl">
      {/* Info banner */}
      <div className="rounded-xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 px-4 py-3">
        <p className="text-[var(--wm-text-ui)] text-blue-700 dark:text-blue-300">
          Credentials are used server-side via Vercel environment variables. These settings are for reference only — set the actual secrets in your Vercel project dashboard.
        </p>
      </div>

      {/* SMS — Twilio */}
      <section className="rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">SMS — Twilio</h3>
            <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">Send estimate & invoice SMS notifications</p>
          </div>
          <Badge configured={smsConfigured} />
        </div>

        <Field
          label="Account SID"
          value={sms.accountSid}
          onChange={v => setSms(s => ({ ...s, accountSid: v }))}
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        />
        <Field
          label="Auth Token"
          type="password"
          value={sms.authToken}
          onChange={v => setSms(s => ({ ...s, authToken: v }))}
          placeholder="Your Twilio Auth Token"
        />
        <Field
          label="From Number"
          value={sms.fromNumber}
          onChange={v => setSms(s => ({ ...s, fromNumber: v }))}
          placeholder="+15551234567"
        />

        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={saveSms}
            className="px-4 py-2 rounded-lg bg-[#2E8BF0] text-white text-[var(--wm-text-body)] font-medium hover:bg-blue-600 transition-colors"
          >
            {smsSaved ? 'Saved' : 'Save'}
          </button>
          <button
            onClick={handleTestSms}
            disabled={!sms.fromNumber || testSmsStatus === 'sending'}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-[#3D5068] text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-50 dark:hover:bg-[#243348] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {testLabel(testSmsStatus, 'Send Test SMS')}
          </button>
        </div>
      </section>

      {/* Email — Resend */}
      <section className="rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Email — Resend</h3>
            <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">Send estimate & invoice email notifications</p>
          </div>
          <Badge configured={emailConfigured} />
        </div>

        <Field
          label="API Key"
          type="password"
          value={email.apiKey}
          onChange={v => setEmail(e => ({ ...e, apiKey: v }))}
          placeholder="re_xxxxxxxxxxxxxxxxxxxx"
        />
        <Field
          label="From Email"
          value={email.fromEmail}
          onChange={v => setEmail(e => ({ ...e, fromEmail: v }))}
          placeholder="noreply@yourdomain.com"
        />

        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={saveEmail}
            className="px-4 py-2 rounded-lg bg-[#2E8BF0] text-white text-[var(--wm-text-body)] font-medium hover:bg-blue-600 transition-colors"
          >
            {emailSaved ? 'Saved' : 'Save'}
          </button>
          <button
            onClick={handleTestEmail}
            disabled={!email.fromEmail || testEmailStatus === 'sending'}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-[#3D5068] text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-50 dark:hover:bg-[#243348] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {testLabel(testEmailStatus, 'Send Test Email')}
          </button>
        </div>
      </section>
    </div>
  );
}
